import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { COMMON_API } from '../../CommonAPI';

const AddProject = () => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    category: [],
    name: '',
    desktopImages: [],
    mobileImages: []
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${COMMON_API}/api/project/categories`);
        setCategories(res.data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = e => {
    const { name, value, checked } = e.target;
    if (name === 'category') {
      const categoryId = parseInt(value);
      setForm(prev => ({
        ...prev,
        category: checked
          ? [...prev.category, categoryId]
          : prev.category.filter(id => id !== categoryId)
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDesktopChange = (index, file) => {
    setForm(prev => {
      const updated = [...prev.desktopImages];
      updated[index] = file;
      return { ...prev, desktopImages: updated };
    });
  };

  const handleMobileChange = (index, file) => {
    setForm(prev => {
      const updated = [...prev.mobileImages];
      updated[index] = file;
      return { ...prev, mobileImages: updated };
    });
  };

  const addDesktopImage = () => {
    setForm(prev => ({
      ...prev,
      desktopImages: [...prev.desktopImages, null]
    }));
  };

  const addMobileImage = () => {
    setForm(prev => ({
      ...prev,
      mobileImages: [...prev.mobileImages, null]
    }));
  };

  const removeDesktopImage = index => {
    setForm(prev => {
      const updated = [...prev.desktopImages];
      updated.splice(index, 1);
      return { ...prev, desktopImages: updated };
    });
  };

  const removeMobileImage = index => {
    setForm(prev => {
      const updated = [...prev.mobileImages];
      updated.splice(index, 1);
      return { ...prev, mobileImages: updated };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem('authToken');
      const formData = new FormData();

      form.category.forEach(catId => formData.append('category', catId));
      formData.append('name', form.name);

      form.desktopImages.forEach((image, index) => {
        if (image) formData.append(`image[${index}]`, image);
      });

      form.mobileImages.forEach((image, index) => {
        if (image) formData.append(`big_image[${index}]`, image);
      });

      await axios.post(`${COMMON_API}/api/project/create-project`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Project added successfully!');
      navigate('/projects');
    } catch (error) {
      console.error(error);
      toast.error('Failed to add project!');
    }
  };

  return (
    <div className="ml-64 p-8 bg-gray-100 min-h-screen">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow space-y-4">
        <h2 className="text-2xl font-semibold mb-4">Add New Project</h2>

        <div>
          <label className="block font-medium mb-1">Select Categories:</label>
          <div className="grid grid-cols-2 gap-2">
            {categories.map(cat => (
              <label key={cat.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="category"
                  value={cat.id}
                  checked={form.category.includes(cat.id)}
                  onChange={handleChange}
                  className="accent-blue-600"
                />
                <span>{cat.category}</span>
              </label>
            ))}
          </div>
        </div>

        <input
          type="text"
          name="name"
          placeholder="Project Name"
          value={form.name}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded"
        />

        {/* Desktop Images Section */}
        <div>
          <h4 className="font-semibold">Desktop Images</h4>
          {form.desktopImages.map((image, index) => (
            <div key={index} className="border p-3 rounded bg-gray-50 mb-2 relative">
              {image && (
                <img
                  src={URL.createObjectURL(image)}
                  alt="Desktop Preview"
                  className="w-32 h-20 object-cover rounded mb-2"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={e => handleDesktopChange(index, e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <button
                type="button"
                onClick={() => removeDesktopImage(index)}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                ✖
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addDesktopImage}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Add Desktop Image
          </button>
        </div>

        {/* Mobile Images Section */}
        <div>
          <h4 className="font-semibold">Mobile Images</h4>
          {form.mobileImages.map((image, index) => (
            <div key={index} className="border p-3 rounded bg-gray-50 mb-2 relative">
              {image && (
                <img
                  src={URL.createObjectURL(image)}
                  alt="Mobile Preview"
                  className="w-32 h-20 object-cover rounded mb-2"
                />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={e => handleMobileChange(index, e.target.files[0])}
                className="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <button
                type="button"
                onClick={() => removeMobileImage(index)}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                ✖
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addMobileImage}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            Add Mobile Image
          </button>
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Submit
        </button>
      </form>
    </div>
  );
};

export default AddProject;
